import { useApolloClient } from "@apollo/client";
import cuid from "cuid";
import dayjs from "dayjs";
import {
  defaults,
  defaultsDeep,
  filter,
  find,
  get,
  head,
  isEmpty,
  isString,
  keyBy,
  map,
  set,
} from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import useZipCodeCities, {
  fetchZipCodeCities,
} from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COST } from "utils/api/graphql/mutations/costs";
import { SYNC_PROJECT_SELLIGENT } from "utils/api/graphql/mutations/projects";
import {
  ACTION_TYPES,
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SCREENS,
  SELLIGENT_STEPS,
  SUBSCRIPTION_STATUSES,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import {
  buildGuestProjectDetailState,
  redirectAfterDuplicateProject,
} from "utils/helpers/project";
import { filterByConditions } from "../../../..";
import Loading from "../Components/Spin";
import View from "./View";

const Subscription = ({ project, tracer, onNext, onBack, updateUrl }) => {
  const client = useApolloClient();
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const [loading, setLoading] = useState(true);
  const subscriptionId =
    get(project, "fields.currentSubscription") ||
    localStorage.getItem(KEYS.SUBSCRIPTION_ID);
  const [initialValues, setInitialValues] = useState(null);
  const [recalculateCostLoading, setRecalculateCostLoading] = useState(false);
  const { cities } = useZipCodeCities(
    get(initialValues, `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`),
  );
  const navigate = useNavigate();

  const findStatus = (data, template) => {
    return find(data, ({ template: _ }) => _ === template);
  };

  const insurancePlans = useMemo(
    () =>
      get(project, "fields.insurancePlans", [])?.map((id) => ({
        AND: [
          {
            insurancePlans: { id },
          },
          { project: { id: project.id } },
        ],
      })),
    [project],
  );
  const attachmentsFilter = {
    where: {
      OR: [
        {
          project: {
            id: project.id,
          },
        },
        ...insurancePlans,
      ],
    },
  };
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS, {
    refetchQueries: [
      {
        query: graphql.queries.ATTACHMENTS,
        awaitRefetchQueries: true,
        variables: attachmentsFilter,
      },
    ],
  });

  const [updateProject] = useMutation(graphql.mutations.UPDATE_PROJECT);
  const [duplicateProject] = useMutation(graphql.mutations.DUPLICATE_PROJECT);
  const [syncProjectWithSelligent] = useMutation(SYNC_PROJECT_SELLIGENT, {
    onError: (error) => console.log(error),
  });
  const duplicateProjectStatus = ({ Template, prevTemplate, fields }) => {
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      Template,
    );
    const prevStatus = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      prevTemplate,
    );
    const actionsToExecute = filterByConditions(
      get(prevStatus || status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));

    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
          fields,
        },
      },
      onCompleted: ({ duplicateProject }) => {
        redirectAfterDuplicateProject({
          duplicateProject,
          tracer,
          statusId: get(status, "id"),
        });
      },
    });
  };

  const updateProjectStatus = ({ template }) => {
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
        },
      },
      onCompleted: ({ updateProject }) => {
        navigate(
          `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${updateProject?.id}`,
          {
            state: buildGuestProjectDetailState({ tracer, status }),
          },
        );
      },
    });
  };

  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id: subscriptionId } },
        },
      ],
    },
  );

  const recalculateCost = ({
    cost,
    issuanceDate,
    oldIssuanceDate,
    setFieldValue,
    setShowChangeCostModal,
    isCancel,
    isRia,
  }) => {
    setRecalculateCostLoading(true);
    const issuanceDateISO = issuanceDate
      ? dayjs(issuanceDate).toISOString()
      : issuanceDate;
    const oldIssuanceDateISO = oldIssuanceDate
      ? dayjs(oldIssuanceDate).toISOString()
      : oldIssuanceDate;
    const formattedIssuanceDate = isString(issuanceDateISO)
      ? moment(issuanceDateISO)
      : issuanceDateISO;

    const formattedOldIssuanceDate = isString(oldIssuanceDateISO)
      ? moment(oldIssuanceDateISO)
      : oldIssuanceDateISO;

    if (
      formattedIssuanceDate.isSame(formattedOldIssuanceDate.format(), "day")
    ) {
      setRecalculateCostLoading(false);
      return;
    }
    updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contract: {
            id: get(project, "contract.id"),
            issuanceDate: issuanceDateISO,
          },
        },
      },
      onCompleted: () => {
        buildGeneralCost({
          onCompleted: ({ buildGeneralCost: generalCost }) => {
            if (!generalCost?.cost) {
              setFieldValue(
                "project.contract.issuanceDate",
                oldIssuanceDateISO,
              );
              setShowChangeCostModal({
                noCost: true,
              });
              return updateProject({
                variables: {
                  where: { id: project.id },
                  data: {
                    contract: {
                      id: get(project, "contract.id"),
                      issuanceDate: oldIssuanceDateISO,
                    },
                  },
                },
                onCompleted: () => {
                  setRecalculateCostLoading(false);
                  buildGeneralCost();
                },
              });
            }
            if (cost === generalCost.cost) {
              setRecalculateCostLoading(false);
              return;
            }
            if (isCancel) {
              setFieldValue("project.contract.issuanceDate", issuanceDateISO);
              refetch();
              setRecalculateCostLoading(false);
              return setShowChangeCostModal({});
            }
            setShowChangeCostModal({
              isRia,
              issuanceDate: {
                old: formattedOldIssuanceDate.format(DATE_FORMAT),
                new: formattedIssuanceDate.format(DATE_FORMAT),
              },
              cost: {
                old: cost,
                new: generalCost.cost,
              },
            });
            refetch();
            setRecalculateCostLoading(false);
          },
        });
      },
    });
  };

  const onPersonalInformationEdit = () => {
    const {
      insurancePlans,
      currentSubscription,
      nextQuoteStatus,
      selectedToCompare,
      ...fields
    } = project?.fields;
    duplicateProjectStatus({
      Template:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
          .ADHERENT_TELECOMS,
      prevTemplate:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
          .ADHERENT_INFO,
      fields,
    });
  };

  const onSocialRegimeEdit = () => {
    const {
      insurancePlans,
      currentSubscription,
      nextQuoteStatus,
      selectedToCompare,
      ...fields
    } = project?.fields;

    duplicateProjectStatus({
      Template:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
          .ADHERENT_INFO,
      prevTemplate:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
          .ADHERENT_INFO,
      fields,
    });
  };

  const onProductEdit = () => {
    // onBack();
    // const {
    //   insurancePlans,
    //   currentSubscription,
    //   nextQuoteStatus,
    //   selectedToCompare,
    //   ...fields
    // } = project?.fields;

    // duplicateProjectStatus({
    //   Template:
    //     TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA].PRODUCTS,
    //   prevTemplate:
    //     TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
    //       .ADHERENT_TELECOMS,
    //   fields,
    // });
    updateProjectStatus({
      template:
        TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
          .PRODUCTS,
    });
  };

  const [getSubscription, { data }] = useLazyQuery(
    graphql.queries.SUBSCRIPTION,
  );

  const [getGeneralCost, { data: generalCost, refetch }] = useLazyQuery(
    graphql.queries.GENERAL_COST,
    {
      variables: {
        where: {
          project: { id: get(project, "id") },
          insurancePlan: {
            id: get(data, "subscription.insurancePlan.id"),
          },
        },
      },
    },
  );

  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST, {
    variables: {
      data: {
        project: { id: project.id },
        insurancePlan: {
          id: get(data, "subscription.insurancePlan.id"),
        },
      },
    },
  });

  const buildInitialValues = (projectData, zipCodeCities = []) => {
    const children = filter(get(projectData, "contact.relatedPersons", []), {
      relationship: RELATIONSHIP.CHILD,
    });
    const spouse = get(projectData, "contact.relatedPersons", []).find(
      ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
    );
    const contactTelecoms = get(projectData, "contact.telecoms", []);
    const telecoms = keyBy(contactTelecoms, "system");
    set(
      telecoms,
      CONTACT_POINT_SYSTEM.BIRTH_COUNTRY,
      get(telecoms, CONTACT_POINT_SYSTEM.BIRTH_COUNTRY, {
        id: cuid(),
        system: CONTACT_POINT_SYSTEM.BIRTH_COUNTRY,
        value: TRANSLATION.DEFAULTS.BIRTH_COUNTRY,
      }),
    );
    const projectWithDefaults = { ...projectData };
    if (isEmpty(get(projectWithDefaults, "contact.nationality")))
      set(
        projectWithDefaults,
        "contact.nationality",
        TRANSLATION.DEFAULTS.NATIONALITY,
      );

    if (zipCodeCities.length === 1)
      set(telecoms, CONTACT_POINT_SYSTEM.CITY, {
        id: cuid(),
        system: CONTACT_POINT_SYSTEM.CITY,
        value: get(head(zipCodeCities), "label"),
      });

    return {
      resiliation:
        get(projectData, "ria") || get(projectData, "fields.selfOccupation"),
      project: projectWithDefaults,
      telecoms,
      children,
      spouse: isEmpty(spouse)
        ? undefined
        : defaultsDeep(spouse, {
            identity: { fields: { teletransmission: true } },
          }),
    };
  };

  const MAX_SUBSCRIPTION_LOAD_RETRIES = 5;
  const SUBSCRIPTION_LOAD_RETRY_DELAY_MS = 400;

  useEffect(() => {
    let isCancelled = false;

    const loadSubscriptionData = async (attempt = 0) => {
      if (!subscriptionId) {
        setInitialValues(buildInitialValues(project));
        setLoading(false);
        return;
      }

      try {
        const { data: queryData } = await getSubscription({
          fetchPolicy: "no-cache",
          variables: { where: { id: subscriptionId } },
        });

        if (isCancelled) return;

        const subscriptionData = get(queryData, "subscription");
        if (
          isEmpty(subscriptionData) &&
          attempt < MAX_SUBSCRIPTION_LOAD_RETRIES
        ) {
          await new Promise((resolve) => {
            setTimeout(resolve, SUBSCRIPTION_LOAD_RETRY_DELAY_MS);
          });
          if (!isCancelled) await loadSubscriptionData(attempt + 1);
          return;
        }

        const projectData = get(subscriptionData, "project") || project;
        const zipCode = get(
          find(get(projectData, "contact.telecoms", []), {
            system: CONTACT_POINT_SYSTEM.ZIP_CODE,
          }),
          "value",
          "",
        );

        let zipCodeCities = [];
        try {
          zipCodeCities = await fetchZipCodeCities(client, zipCode);
        } catch {
          // Continue without city lookup
        }

        if (isCancelled) return;

        if (!isEmpty(subscriptionData)) getGeneralCost();
        setInitialValues(buildInitialValues(projectData, zipCodeCities));
        setLoading(false);
      } catch {
        if (!isCancelled) {
          setInitialValues(buildInitialValues(project));
          setLoading(false);
        }
      }
    };

    loadSubscriptionData();

    return () => {
      isCancelled = true;
    };
  }, [subscriptionId]);
  const onSubmit = ({ telecoms, spouse, children, project: projectValues }) => {
    const isEffectiveDateChanged = !dayjs(
      get(projectValues, "contract.issuanceDate"),
    ).isSame(dayjs(get(project, "contract.issuanceDate")));
    const subscription = omitDeep(get(data, "subscription"), "__typename");
    set(telecoms, CONTACT_POINT_SYSTEM.ADDRESS, {
      id: cuid(),
      system: CONTACT_POINT_SYSTEM.ADDRESS,
      value: get(telecoms, "ADDRESS.value"),
    });
    const contactTelecoms = map(telecoms, (item, key) =>
      defaults(item, { id: cuid(), system: key }),
    );
    set(subscription, "project", {
      ...projectValues,
      terminationRequest: {
        id: cuid(),
        ...projectValues.terminationRequest,
        brokerageMandate: {
          id: cuid(),
          ...projectValues.terminationRequest?.brokerageMandate,
        },
      },
    });
    set(subscription, "project.contact.telecoms", contactTelecoms);
    const relatedPersons = [
      ...(!isEmpty(spouse)
        ? [
            defaultsDeep(spouse, {
              id: cuid(),
              relationship: RELATIONSHIP.SPOUSE,
              identity: { id: cuid(), user: { id: cuid() } },
            }),
          ]
        : []),
      ...children.map((child) =>
        defaultsDeep(child, {
          id: cuid(),
          relationship: RELATIONSHIP.CHILD,
          identity: { id: cuid(), user: { id: cuid() } },
        }),
      ),
    ];
    set(subscription, "project.contact.relatedPersons", relatedPersons);
    set(
      subscription,
      "project.contract.caseType",
      get(subscription, "project.ria")
        ? CASE_TYPES.RETURN_TO_COMPETITION
        : CASE_TYPES.NEW_CONTRACT,
    );
    set(subscription, "status", SUBSCRIPTION_STATUSES.IN_PROGRESS);
    const issuanceDate = get(subscription, "project.contract.issuanceDate");
    if (issuanceDate) {
      set(
        subscription,
        "project.contract.issuanceDate",
        dayjs(issuanceDate).toISOString(),
      );
    }
    updateSubscription({
      variables: {
        where: {
          id: subscriptionId,
        },
        data: omitDeep(subscription, "__typename"),
      },
      onCompleted: (_) => {
        const source = localStorage.getItem(KEYS.SOURCE);
        if (isEffectiveDateChanged)
          generateQuoteDocuments({
            variables: {
              data: {
                project: { id: get(project, "id") },
                insurancePlans: [{ id: get(subscription, "insurancePlan.id") }],
              },
            },
          });
        updateUrl({
          subscriptionId,
          url: get(
            data,
            "subscription.insurancePlan.config.generatedUrl",
            `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
          ),
        });
        const phone = get(
          contactTelecoms.find(
            ({ system }) => system === CONTACT_POINT_SYSTEM.PHONE,
          ),
          "value",
        );
        if (phone) {
          syncProjectWithSelligent({
            variables: {
              project: { id: get(project, "id") },
              insurancePlan: { id: get(subscription, "insurancePlan.id") },
              step: SELLIGENT_STEPS.PROPOSITION,
            },
          });
        }
        onNext({
          payload: {},
        });
      },
    });
  };

  if (loading || !initialValues) return <Loading />;

  return (
    <View
      subscription={get(data, "subscription")}
      onBack={onBack}
      initialValues={initialValues}
      generalCost={get(generalCost, "generalCost")}
      onPersonalInformationEdit={onPersonalInformationEdit}
      onSocialRegimeEdit={onSocialRegimeEdit}
      onProductEdit={onProductEdit}
      onSubmit={onSubmit}
      cities={cities}
      translate={TRANSLATION}
      recalculateCost={recalculateCost}
      recalculateCostLoading={recalculateCostLoading}
    />
  );
};

export default Subscription;
